import { useMemo, useState } from 'react'
import {
  Box,
  CircularProgress,
  Grid,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
} from '@mui/material'
import { useIntl } from 'react-intl'
import { ConsignmentInfoPanel } from '../../../components/ConsignmentInfoPanel'
import InfoDisplayItem from '../../../components/Typographies/InfoDisplayItem'
import { LabelItem } from '../../../components/Typographies/LabelItem'
import SideModalWrapper from '../../Assets/modals/SideModalWrapper'
import { messages } from '../messages'
import { UnassignedConsignment, UnassignedLoad, UnassignedLoadContents } from '../types'
import ContentDetailsSidePanel from './ContentDetailsSidePanel'
import { useGetUnassignedLoadContentsQuery } from '../../../app/redux-fetch/apiOrders'
import { useGetCountriesQuery } from '../../../app/redux-fetch/apiCoreQuery'

type LoadContentsSidePanelProps = {
  open: boolean
  onClose: () => void
  consignment: UnassignedConsignment | null
  load: UnassignedLoad | null
  tenantId: number
}

const LoadContentsSidePanel = ({
  open,
  onClose,
  consignment,
  load,
  tenantId,
}: LoadContentsSidePanelProps) => {
  const { formatMessage } = useIntl()

  // Lazy load contents when the panel is open and we have a load
  const hasValue = load?.value !== null && load?.value !== undefined
  const { data: contents, isLoading: isLoadingContents } = useGetUnassignedLoadContentsQuery(
    { tenantId, loadId: load?.loadId ?? 0 },
    { skip: !open || !load || hasValue }
  )

  // Fetch countries from CORE to resolve country of origin names
  const { data: countries } = useGetCountriesQuery(undefined, { skip: !open || !load || hasValue })

  // Build a lookup map for country names by ID
  const countryLookup = useMemo((): Map<number, string> => {
    if (!countries) return new Map<number, string>()
    return new Map<number, string>(countries.map((c: { id: number; name: string }) => [c.id, c.name] as [number, string]))
  }, [countries])

  // Helper function to get country name from ID
  const getCountryName = (countryOfOriginId?: number, countryOfOriginName?: string): string => {
    if (countryOfOriginId && countryLookup.has(countryOfOriginId)) {
      const name = countryLookup.get(countryOfOriginId)
      if (name) return name
    }
    return countryOfOriginName ?? formatMessage(messages.notAvailable)
  }

  // State for content details side panel
  const [selectedContent, setSelectedContent] = useState<UnassignedLoadContents | null>(null)
  const [contentDetailsOpen, setContentDetailsOpen] = useState(false)

  const handleContentClick = (content: UnassignedLoadContents) => {
    setSelectedContent(content)
    setContentDetailsOpen(true)
  }

  const handleContentDetailsClose = () => {
    setContentDetailsOpen(false)
    setSelectedContent(null)
  }

  if (!consignment || !load) return null

  const hasContents = contents && contents.length > 0

  return (
    <SideModalWrapper
      open={open}
      onClose={onClose}
      headerLabel={formatMessage(messages.contents)}
      withBottomBox={false}
      width="62.5%"
    >
      <Box sx={{ p: '32px' }}>
        <ConsignmentInfoPanel
          data={{
            shipperName: consignment.shipperName,
            loadTypeName: consignment.loadTypeName,
            description: consignment.description,
            currencyCode: consignment.currencyCode,
            trackingNumber: consignment.trackingNumber,
            customerReferenceNumber: consignment.referenceNumber,
          }}
          variant="sidePanel"
        />

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            border: '1px solid #ccc',
            borderLeft: '4px solid #636363',
            backgroundColor: '#ffffff',
            borderRadius: '4px',
            padding: '16px',
            gap: '8px',
            marginBottom: '16px',
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <LabelItem>{formatMessage(messages.loadInformation)}</LabelItem>
            </Grid>
            <Grid item xs={12} md={3}>
              <InfoDisplayItem
                label={formatMessage(messages.trackingNumber)}
                value={load.trackingNumber ?? 'N/A'}
                isSmallScreen={false}
                allowOverlap={true}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <InfoDisplayItem
                label={formatMessage(messages.numberOfLoads)}
                value={load.quantity}
                isSmallScreen={false}
                allowOverlap={true}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <InfoDisplayItem
                label={formatMessage(messages.loadType)}
                value={load.loadTypeName ?? 'N/A'}
                isSmallScreen={false}
                allowOverlap={true}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <InfoDisplayItem
                label={formatMessage(messages.tempRange)}
                value={load.temperatureRange ?? 'N/A'}
                isSmallScreen={false}
                allowOverlap={true}
              />
            </Grid>
          </Grid>
        </Box>

        {hasValue ? (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              border: '1px solid #ccc',
              borderLeft: '4px solid #636363',
              backgroundColor: '#ffffff',
              borderRadius: '4px',
              padding: '16px',
              gap: '8px',
              marginBottom: '16px',
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <LabelItem>{formatMessage(messages.value)}</LabelItem>
              </Grid>
              <Grid item xs={12} md={4}>
                <InfoDisplayItem
                  label={formatMessage(messages.totalValue)}
                  value={load.value?.toString() ?? 'N/A'}
                  isSmallScreen={false}
                  allowOverlap={true}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InfoDisplayItem
                  label={formatMessage(messages.description)}
                  value={load.description ?? 'N/A'}
                  isSmallScreen={false}
                  allowOverlap={true}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InfoDisplayItem
                  label={formatMessage(messages.containsHazardousMaterials)}
                  value={load.isHazardous ? formatMessage(messages.yes) : formatMessage(messages.no)}
                  isSmallScreen={false}
                  allowOverlap={true}
                />
              </Grid>
            </Grid>
          </Box>
        ) : (
          <>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              {formatMessage(messages.contents)}
            </Typography>

            {isLoadingContents ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{formatMessage(messages.productDescription)}</TableCell>
                      <TableCell>{formatMessage(messages.quantity)}</TableCell>
                      <TableCell>{formatMessage(messages.unitPrice)}</TableCell>
                      <TableCell>{formatMessage(messages.total)}</TableCell>
                      <TableCell>{formatMessage(messages.countryOfOrigin)}</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {hasContents ? (
                      contents?.map((content) => (
                        <TableRow key={content.id}>
                          <TableCell>{content.description}</TableCell>
                          <TableCell>{content.quantity}</TableCell>
                          <TableCell>
                            {content.unitPrice !== null && content.unitPrice !== undefined
                              ? content.unitPrice.toFixed(2)
                              : formatMessage(messages.notAvailable)}
                          </TableCell>
                          <TableCell>
                            {content.total !== null && content.total !== undefined
                              ? content.total.toFixed(2)
                              : formatMessage(messages.notAvailable)}
                          </TableCell>
                          <TableCell>
                            {getCountryName(content.countryOfOriginId, content.countryOfOriginName)}
                          </TableCell>
                          <TableCell>
                            <Link
                              component="button"
                              variant="body2"
                              onClick={() => handleContentClick(content)}
                              sx={{ cursor: 'pointer' }}
                            >
                              {formatMessage(messages.view)}
                            </Link>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          {formatMessage(messages.noContents)}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Box>
      <ContentDetailsSidePanel
        open={contentDetailsOpen}
        onClose={handleContentDetailsClose}
        content={selectedContent}
        countryName={getCountryName(selectedContent?.countryOfOriginId, selectedContent?.countryOfOriginName)}
      />
    </SideModalWrapper>
  )
}

export default LoadContentsSidePanel
